import { css } from "@emotion/react";

import {
    AlertIcon,
    CheckCircleIcon,
    CloseIcon,
    colors,
    InfoIcon,
    WarningIcon
} from "@dotnet/telematics-design-system";

import { svgFontSize } from "shared";

import Button, {
    BUTTON_COLORS,
    BUTTON_SIZES,
    BUTTON_VARIANTS
} from "../Button";
import { AlertProps } from "./types";

const Alert = ({
    status,
    title,
    description,
    primaryBtn,
    secondaryBtn,
    close
}: AlertProps) => {
    const { blue700, gray700, gray900, green600, orange600, red700, white } =
        colors;

    const styleOptions = {
        info: {
            color: blue700,
            icon: <InfoIcon />
        },
        critical: {
            color: red700,
            icon: <AlertIcon />
        },
        warning: {
            color: orange600,
            icon: <WarningIcon />
        },
        success: {
            color: green600,
            icon: <CheckCircleIcon />
        }
    };

    const color = styleOptions[status].color;
    const iconImage = styleOptions[status].icon;

    return (
        <div
            css={css({
                display: "flex",
                alignItems: "center",
                padding: "12px 12px 12px 8px",
                backgroundColor: white,
                borderLeft: `4px solid ${color}`,
                borderRadius: "4px",
                wordBreak: "break-word",
                boxShadow:
                    "0px 4px 4px rgba(0, 0, 0, 0.25), 0px 0px 3px rgba(0, 0, 0, 0.1)",

                "& > svg:first-of-type": {
                    color,
                    alignSelf: "flex-start",
                    marginRight: "8px"
                }
            })}
        >
            {iconImage}

            <div
                css={css({
                    flex: "1",
                    fontSize: "14px",
                    letterSpacing: "0.1px",
                    lineHeight: "20px"
                })}
            >
                <div
                    css={css({
                        color: gray900,
                        fontWeight: "500"
                    })}
                >
                    {title}
                </div>

                {description && (
                    <div
                        css={css({
                            color: gray700,
                            marginTop: "4px"
                        })}
                    >
                        {description}
                    </div>
                )}

                {(primaryBtn || secondaryBtn) && (
                    <div
                        css={css({
                            display: "flex",
                            columnGap: "8px",
                            marginTop: "12px"
                        })}
                    >
                        {primaryBtn && (
                            <Button
                                fullWidth
                                variant={BUTTON_VARIANTS.TextOnly}
                                color={BUTTON_COLORS.Primary}
                                size={BUTTON_SIZES.Small}
                                onClick={primaryBtn.action}
                            >
                                {primaryBtn.label}
                            </Button>
                        )}

                        {secondaryBtn && (
                            <Button
                                fullWidth
                                variant={BUTTON_VARIANTS.TextOnly}
                                color={BUTTON_COLORS.Secondary}
                                size={BUTTON_SIZES.Small}
                                onClick={secondaryBtn.action}
                            >
                                {secondaryBtn.label}
                            </Button>
                        )}
                    </div>
                )}
            </div>

            {close && (
                <CloseIcon
                    onClick={close}
                    css={css({
                        alignSelf: "flex-start",
                        cursor: "pointer",
                        margin: "-4px -4px 0 8px",
                        ...svgFontSize(16)
                    })}
                />
            )}
        </div>
    );
};

export default Alert;
